import type { CSSProperties } from 'react';
import { useReducedMotion } from '../lib/useReducedMotion.js';

/**
 * The living star field: slow celestial snow behind the whole app.
 *
 * Three particle families with genuinely different behaviour, so the layer
 * never reads as one repeated loop:
 *   A. micro stardust — dim points drifting down, wandering, recycling
 *   B. four-point sparkles — Astral's existing mark, drifting/rotating, plus a
 *      stationary variant that fades in, brightens and fades out
 *   C. bright motes — a rare, brief flare
 *
 * Every descriptor is generated ONCE at module scope from a seeded PRNG, so
 * positions are stable across renders and re-mounts (no Math.random() during
 * render, no re-render churn). Motion is pure CSS on transform/opacity — React
 * never runs an animation loop, so nothing can outlive the component.
 */

function rng(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (Math.imul(s, 1103515245) + 12345) & 0x7fffffff;
    return s / 0x7fffffff;
  };
}

const R = (n: number): number => Math.round(n * 100) / 100;

interface Dust {
  left: number;
  size: number;
  drift: number;
  dur: number;
  delay: number;
  peak: number;
  halo: number;
  warm: boolean;
}

/** A. Micro stardust — tiny, dim, always falling, each with a small bloom. */
const DUST: Dust[] = (() => {
  const rnd = rng(0x51d3a7);
  return Array.from({ length: 34 }, () => {
    const size = R(0.8 + rnd() * 1.5);
    return {
    left: R(rnd() * 100),
    size,
    // Bloom scales with the point: ~2px for the faintest, ~4.5px for the largest.
    halo: R(2 + (size / 2.3) * 2.5),
    // Lateral wander over the whole fall, in vw so it scales with the viewport.
    drift: R((rnd() - 0.5) * 9),
    dur: R(46 + rnd() * 62), // very slow: 46–108s top to bottom
    delay: R(-rnd() * 108), // negative → mid-flight on first paint, desynced
    peak: R(0.22 + rnd() * 0.5),
    warm: rnd() < 0.35, // a third carry a pale-pink tint
    };
  });
})();

interface Spark {
  left: number;
  top: number;
  size: number;
  dur: number;
  delay: number;
  peak: number;
  spin: number;
  halo: number;
  drifting: boolean;
  metallic: boolean;
}

/** B. Four-point sparkles — half drift, half breathe in place. */
const SPARKS: Spark[] = (() => {
  const rnd = rng(0x2f8c41);
  return Array.from({ length: 17 }, (_, i) => {
    const size = R(3 + rnd() * 4.5);
    return {
      left: R(rnd() * 96 + 2),
      top: R(rnd() * 94 + 3),
      size,
      dur: R(15 + rnd() * 20),
      delay: R(-rnd() * 35),
      peak: R(0.4 + rnd() * 0.55),
      spin: R(18 + rnd() * 40) * (rnd() < 0.5 ? -1 : 1),
      // Perceived bloom scales with the sparkle: ~5px small → ~9px large.
      halo: R(5 + (size / 7.5) * 4),
      drifting: i % 2 === 0,
      metallic: rnd() < 0.3,
    };
  });
})();

interface Mote {
  left: number;
  top: number;
  size: number;
  dur: number;
  delay: number;
}

/** C. Bright motes — infrequent, brief, unpredictable. */
const MOTES: Mote[] = (() => {
  const rnd = rng(0x9ab134);
  return Array.from({ length: 5 }, () => ({
    left: R(rnd() * 92 + 4),
    top: R(rnd() * 86 + 6),
    size: R(1.8 + rnd() * 1.8),
    dur: R(17 + rnd() * 23), // long cycle, tiny visible window
    delay: R(-rnd() * 40),
  }));
})();

/**
 * Mounted once at app level, behind every screen. `pointer-events: none` and a
 * z-index below the UI layer mean it can never intercept a tap.
 */
export function Stardust(): JSX.Element | null {
  const reduced = useReducedMotion();

  return (
    <div className={`stardust${reduced ? ' stardust--static' : ''}`} aria-hidden="true">
      {DUST.map((d, i) => (
        <span
          key={`d${i}`}
          className={`stardust__dust${d.warm ? ' stardust__dust--warm' : ''}`}
          style={
            {
              left: `${d.left}%`,
              width: d.size,
              height: d.size,
              '--peak': d.peak,
              '--halo': `${d.halo}px`,
              '--drift': `${d.drift}vw`,
              animationDuration: `${d.dur}s`,
              animationDelay: `${d.delay}s`,
            } as CSSProperties
          }
        />
      ))}

      {SPARKS.map((s, i) => (
        <span
          key={`s${i}`}
          className={`stardust__spark${s.drifting ? '' : ' stardust__spark--breathe'}${
            s.metallic ? ' stardust__spark--metallic' : ''
          }`}
          style={
            {
              left: `${s.left}%`,
              top: `${s.top}%`,
              width: s.size,
              height: s.size,
              '--peak': s.peak,
              '--spin': `${s.spin}deg`,
              '--halo': `${s.halo}px`,
              animationDuration: `${s.dur}s`,
              animationDelay: `${s.delay}s`,
            } as CSSProperties
          }
        />
      ))}

      {MOTES.map((m, i) => (
        <span
          key={`m${i}`}
          className="stardust__mote"
          style={
            {
              left: `${m.left}%`,
              top: `${m.top}%`,
              width: m.size,
              height: m.size,
              animationDuration: `${m.dur}s`,
              animationDelay: `${m.delay}s`,
            } as CSSProperties
          }
        />
      ))}
    </div>
  );
}
